import {
  FC,
  memo,
  useEffect,
  useRef,
  useState,
  useMemo,
  useContext
} from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import type { NavigationItem } from '@/network/graphql.g'
import Link from '@/components/atoms/link'
import styled from 'styled-components'
import MenuItem from '@/components/atoms/menuTextItem'
import { useMainNavigationDispatch } from '@/providers/mainNavigationMenuProvider'
import ToolbarLinkLogo from '../molecules/toolbarLinkLogo'
import { ContentPaddings } from './pageContentWrapper'
import { useAsset } from '@/providers/assetsProvider'
import DesktopDropdownMenuRedesign from '@/components/organisms/desktopDropdownMenuRedesign'
import ToolbarRedesign from '@/components/organisms/toolbar/toolbarRedesign'
import { useUser } from '@/providers/userProvider'
import useCurrentMainCategoryId, {
  lastVisitedMainCategoryKey,
  MainCategoryId
} from '../../hooks/useCurrentMainCategoryId'
import { StoreContext } from '@/providers/storeProvider'

type DesktopCategoryMenuRedesignWrapperProps = {
  navigationItems: NavigationItem[]
  otherMenuItems?: NavigationItem[]
}

const DesktopCategoryMenuRedesignWrapper: FC<DesktopCategoryMenuRedesignWrapperProps> =
  ({ navigationItems, otherMenuItems }) => {
    const { isLoggedIn } = useUser()
    const { isFrogies } = useContext(StoreContext)
    const { isCartProcessStep } = useAsset()

    const processedNavigationItems = useMemo(() => {
      if (isFrogies && navigationItems) {
        return navigationItems.map((item) => ({
          ...item,
          name: item.name.charAt(0).toUpperCase() + item.name.slice(1)
        }))
      }
      return navigationItems
    }, [navigationItems, isFrogies])

    const currentMainCategoryId = useCurrentMainCategoryId(
      processedNavigationItems
    )
    const isSportAndOthers =
      currentMainCategoryId === MainCategoryId.Sports ||
      currentMainCategoryId === MainCategoryId.Accessories
    return (
      <Wrapper
        px={{
          wide: ContentPaddings.wide
        }}
        isCartProcessStep={isCartProcessStep}
        isSportAndOthers={isSportAndOthers}
      >
        {!isCartProcessStep && (
          <Flex>
            <DesktopCategoryRedesignMenu
              showCart={true}
              navigationItems={processedNavigationItems}
              otherMenuItems={otherMenuItems}
              currentMainCategoryId={currentMainCategoryId}
            />
          </Flex>
        )}
        <Box mr={!isLoggedIn ? 76 : 0} ml={isLoggedIn ? 38 : 0}>
          <ToolbarLinkLogo />
        </Box>
        <ToolbarRedesign data-testid="headerTabletOrMobile" />
      </Wrapper>
    )
  }

type DesktopCategoryMenuRedesignProps = {
  navigationItems: NavigationItem[]
  otherMenuItems?: NavigationItem[]
  showCart: boolean
  currentMainCategoryId: string
}

const DesktopCategoryRedesignMenu: FC<DesktopCategoryMenuRedesignProps> = memo(
  ({ navigationItems, otherMenuItems, currentMainCategoryId }) => {
    const { slugPath } = useMainNavigationDispatch()
    const [activeItem, setActiveItem] = useState<string | null>(null)
    const [lastVisitedMainCategory, setLastVisitedMainCategory] =
      useState<string | null>(null)

    useEffect(() => {
      try {
        const storedId = localStorage.getItem(lastVisitedMainCategoryKey)
        if (storedId) {
          setLastVisitedMainCategory(storedId)
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
    }, [])

    const handleMouseEnter = (categoryId: string) => {
      setActiveItem(categoryId)
    }
    const handleMouseLeave = () => {
      setActiveItem(null)
    }

    const isAnyActiveInUrl = navigationItems
      ?.filter((i) => i.isMainCategory)
      .some(({ categoryId }) =>
        slugPath.find((path) => String(path.id) === String(categoryId))
      )

    const handleCategoryClick = (categoryId: string) => {
      const idString = String(categoryId)
      try {
        localStorage.setItem(lastVisitedMainCategoryKey, idString)
        // eslint-disable-next-line no-empty
      } catch {}
      setLastVisitedMainCategory(idString)
    }

    const prevMainIdRef = useRef<string | null>(null)

    const currentMainCategory = navigationItems
      ?.filter(({ isMainCategory }) => isMainCategory)
      .find(({ categoryId }) => slugPath.some((p) => p.id === categoryId))

    useEffect(() => {
      if (currentMainCategory?.categoryId) {
        prevMainIdRef.current = currentMainCategory.categoryId
      } else if (lastVisitedMainCategory) {
        prevMainIdRef.current = lastVisitedMainCategory
      }
    }, [currentMainCategory?.categoryId, lastVisitedMainCategory])

    return (
      <StyledNav data-testid="headerDesktopNavigation">
        <StyledUl data-testid="headerDesktopCategoryMenu">
          {navigationItems &&
            navigationItems
              .filter(
                ({ isMainCategory, categoryId }) =>
                  isMainCategory &&
                  categoryId !== MainCategoryId.Sports &&
                  categoryId !== MainCategoryId.Accessories
              )
              .map(
                (
                  {
                    name,
                    children,
                    url,
                    homeBanners,
                    isHighlight,
                    desktopBadge,
                    categoryId
                  },
                  index
                ) => {
                  const cIdString = String(categoryId)
                  const isInSlugPath = slugPath.some(
                    (path) => String(path.id) === cIdString
                  )
                  const isStoredActive =
                    !isAnyActiveInUrl && lastVisitedMainCategory === cIdString

                  const isDefault =
                    !isAnyActiveInUrl && !lastVisitedMainCategory && index === 0

                  const isActive = isInSlugPath || isStoredActive || isDefault

                  return (
                    <StyledLi
                      key={'desktopddropdown' + url}
                      data-testid="headerDesktopCategoryItem"
                      onMouseEnter={() => handleMouseEnter(cIdString)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <StyledLink
                        href={url}
                        onClick={() => handleCategoryClick(cIdString)}
                      >
                        <StyledCapitalized
                          highlight={isHighlight}
                          withBadge={desktopBadge}
                          isActive={isActive}
                        >
                          {name}
                        </StyledCapitalized>
                      </StyledLink>
                      {children?.length > 0 && (
                        <StyledDesktopDropdownRedesignMenu
                          subCategories={children}
                          name={name}
                          activeItem={activeItem}
                          isActive={isActive}
                          otherMenuItems={otherMenuItems}
                          banners={homeBanners}
                          currentMainCategoryId={currentMainCategoryId}
                        />
                      )}
                    </StyledLi>
                  )
                }
              )}
        </StyledUl>
      </StyledNav>
    )
  },
  (prev, next) =>
    prev.showCart === next.showCart &&
    prev.currentMainCategoryId === next.currentMainCategoryId &&
    prev.navigationItems === next.navigationItems
)

export default DesktopCategoryMenuRedesignWrapper

const Wrapper = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  height: ${(props) =>
    props.isSportAndOthers
      ? '50px'
      : !props.isCartProcessStep
      ? '96px'
      : '70px'};
`

const StyledNav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: stretch;
`

const StyledUl = styled.ul`
  display: flex;
  width: 100%;
  margin: 0;
  padding: 0;
`

const StyledDesktopDropdownRedesignMenu = styled(DesktopDropdownMenuRedesign)`
  width: 100%;
  box-sizing: border-box;
  z-index: ${(props) => (props.isActive ? '100' : '-1')};
  opacity: ${(props) => (!props.isActive ? '0' : '1')};
  visibility: ${(props) => (!props.isActive ? 'hidden' : 'visible')};
  pointer-events: ${(props) => (!props.isActive ? 'none' : 'auto')};
  transition: opacity 0.2s, visibility 0.2s;
  display: ${(props) => (props.isActive ? 'block' : 'none')};
  transition-delay: 0s;
`

const StyledLink = styled(Link)`
  height: 100%;
  display: flex;
  margin-right: 0;
  align-items: center;
`

const StyledCapitalized = styled(MenuItem)`
  padding: 6px 8px;
  margin: 0 8px;
  color: ${(props) =>
    props.isActive
      ? props.theme.colors.background
      : props.theme.colors.onBackground};
  background-color: ${(props) =>
    props.isActive ? props.theme.colors.buttonColorNew : 'transparent'};
  border-radius: 4px;
  font-weight: ${(props) => (props.isActive ? '700' : '400')};
  align-self: center;
  font-size: 16px;
  transform: translateY(0) translateX(0);
  &:hover {
    ${(props) =>
      !props.isActive &&
      `
        background-color: ${props.theme.colors.navMenuGrey};
      `}
  }
  ${(props) =>
    props.withBadge
      ? `
        && {
          margin-left: 10px;
        }
      `
      : ''}
`

const StyledLi = styled.li`
  height: 100%;
  display: flex;
  &:hover ${StyledCapitalized}::before {
    transform: translateY(100%) translateX(-50%);
  }
`
